<script setup>
import { computed, ref, onMounted } from 'vue'
import { useLocaleStore } from '@/stores/localeStore'
import cvData from '@/data/cv-data.json'
import NextButton from '../NextButton.vue'
import PreviousButton from '../PreviousButton.vue'

defineEmits(['open-window', 'minimize-window'])

const localeStore = useLocaleStore()
const currentLocale = computed(() => localeStore.currentLocale)

// Computed property to get the localized education data
const educationData = computed(() => {
  return cvData[currentLocale.value]?.education || []
})

// Animation states for each step
const visibleSteps = ref([])

onMounted(() => {
  // Animate steps sequentially
  educationData.value.forEach((_, index) => {
    setTimeout(() => {
      visibleSteps.value.push(index)
    }, index * 200)
  })
})
</script>

<template>
  <div class="relative right-0 h-content-window overflow-hidden">
    <div class="w-full h-full bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 overflow-auto pb-28">
      <div class="max-w-5xl mx-auto p-6 md:p-10">
        <!-- Header -->
        <div class="bg-white/90 backdrop-blur-sm rounded-3xl p-6 md:p-8 shadow-2xl border border-gray-100 mb-8">
          <h1 class="font-trebuchet-pixel text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            {{ $t('windows.academicPath.title') }}
          </h1>
          <hr class="border-t-2 border-gray-300 mb-4" />
          <p class="text-sm md:text-base text-gray-700 leading-relaxed">
            {{ $t('windows.academicPath.description') }}
          </p>
        </div>

        <!-- Timeline avec design évolutif -->
        <div class="relative">
          <!-- Ligne de timeline -->
          <div class="absolute left-8 md:left-12 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-300 via-indigo-400 to-purple-500 hidden md:block"></div>

          <!-- Étapes du parcours -->
          <div class="space-y-8">
            <div
              v-for="(education, index) in educationData"
              :key="index"
              class="relative pl-20 md:pl-24 transition-all duration-700"
              :class="{
                'opacity-0 translate-x-[-20px]': !visibleSteps.includes(index),
                'opacity-100 translate-x-0': visibleSteps.includes(index)
              }"
            >
              <!-- Point sur la timeline -->
              <div
                class="absolute left-6 md:left-10 w-6 h-6 rounded-full border-4 border-white shadow-lg z-10 transition-all duration-500"
                :class="{
                  'bg-gradient-to-br from-blue-500 to-indigo-600 scale-100': visibleSteps.includes(index),
                  'bg-gray-300 scale-75': !visibleSteps.includes(index)
                }"
                :style="{
                  top: '0.5rem'
                }"
              >
                <div v-if="visibleSteps.includes(index)" class="absolute inset-0 rounded-full bg-blue-400 animate-ping opacity-75"></div>
              </div>

              <!-- Carte d'étape -->
              <div
                class="bg-white/95 backdrop-blur-sm rounded-2xl p-6 md:p-8 shadow-xl border border-gray-200 hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] relative overflow-hidden"
                :class="{
                  'ring-2 ring-blue-400': index === 0,
                  'ring-2 ring-indigo-400': index === 1,
                  'ring-2 ring-purple-400': index === 2,
                  'ring-2 ring-gray-400': index >= 3
                }"
              >
                <!-- Barre colorée sur le côté -->
                <div
                  class="absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl transition-all duration-500"
                  :class="{
                    'bg-gradient-to-b from-blue-500 to-blue-600': index === 0,
                    'bg-gradient-to-b from-indigo-500 to-indigo-600': index === 1,
                    'bg-gradient-to-b from-purple-500 to-purple-600': index === 2,
                    'bg-gradient-to-b from-gray-400 to-gray-500': index >= 3
                  }"
                ></div>

                <!-- Badge "En cours" ou année -->
                <div class="flex items-start justify-between mb-4">
                  <div class="flex-1">
                    <div class="flex items-center gap-3 mb-2">
                      <div
                        class="px-4 py-1.5 rounded-full text-xs font-semibold border-2 transition-all duration-300"
                        :class="{
                          'bg-blue-100 text-blue-700 border-blue-300 animate-pulse': index === 0,
                          'bg-indigo-100 text-indigo-700 border-indigo-300': index === 1,
                          'bg-purple-100 text-purple-700 border-purple-300': index === 2,
                          'bg-gray-100 text-gray-700 border-gray-300': index >= 3
                        }"
                      >
                        {{ education.year }}
                      </div>
                      <div
                        v-if="index === 0"
                        class="px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-green-400 to-emerald-500 text-white border-2 border-green-300 shadow-md"
                      >
                        Actuel
                      </div>
                    </div>
                    <h3 class="font-trebuchet-pixel text-xl md:text-2xl font-bold text-gray-900 mb-2">
                      {{ education.degree }}
                    </h3>
                    <p class="font-trebuchet-pixel text-sm md:text-base text-indigo-600 font-semibold mb-3">
                      {{ education.institution }}
                    </p>
                  </div>

                  <!-- Icône de niveau -->
                  <div
                    class="ml-4 p-3 rounded-xl transition-all duration-300"
                    :class="{
                      'bg-gradient-to-br from-blue-100 to-blue-200': index === 0,
                      'bg-gradient-to-br from-indigo-100 to-indigo-200': index === 1,
                      'bg-gradient-to-br from-purple-100 to-purple-200': index === 2,
                      'bg-gradient-to-br from-gray-100 to-gray-200': index >= 3
                    }"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="28"
                      height="28"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      :class="{
                        'text-blue-600': index === 0,
                        'text-indigo-600': index === 1,
                        'text-purple-600': index === 2,
                        'text-gray-600': index >= 3
                      }"
                    >
                      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                    </svg>
                  </div>
                </div>

                <!-- Description -->
                <p v-if="education.description" class="text-sm md:text-base text-gray-700 leading-relaxed mb-4">
                  {{ education.description }}
                </p>

                <!-- Matières principales -->
                <div v-if="education.mainSubjects && education.mainSubjects.length > 0" class="mt-4">
                  <h4 class="font-trebuchet-pixel text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      class="text-indigo-500"
                    >
                      <path d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    {{ $t('windows.cv.mainSubjects') }}
                  </h4>
                  <div class="flex flex-wrap gap-2">
                    <span
                      v-for="(subject, subjectIndex) in education.mainSubjects"
                      :key="subjectIndex"
                      class="px-3 py-1.5 rounded-lg text-xs font-medium bg-gradient-to-r from-gray-50 to-gray-100 text-gray-700 border border-gray-200 hover:border-indigo-300 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-indigo-100 hover:text-indigo-700 transition-all duration-200 shadow-sm"
                    >
                      {{ subject }}
                    </span>
                  </div>
                </div>

                <!-- Lien RNCP si disponible -->
                <div v-if="education.rncpLink" class="mt-4">
                  <a
                    :href="education.rncpLink"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-all duration-200 text-xs font-semibold border border-indigo-200"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3" />
                    </svg>
                    {{ $t('windows.cv.viewRNCP') }}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <PreviousButton current-window-id="academicPath" @open-window="$emit('open-window', $event)" @minimize-window="$emit('minimize-window', $event)" />
      <NextButton current-window-id="academicPath" @open-window="$emit('open-window', $event)" @minimize-window="$emit('minimize-window', $event)" />
    </div>
  </div>
</template>

<style scoped>
@keyframes ping {
  75%,
  100% {
    transform: scale(2);
    opacity: 0;
  }
}

.animate-ping {
  animation: ping 1s cubic-bezier(0, 0, 0.2, 1) infinite;
}
</style>
